import { useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Animated,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { Feather } from "@expo/vector-icons";
import moment from "moment";

import { useAppTheme } from "../../hooks/useAppTheme";
import { useTranslation } from "../../utils/useTranslation";
import { translations } from "../../utils/translations";
import { exportDataToExcel } from "../../utils/exportDataToExcel";
import { getMergedTrackerOrders } from "../../redux/dispatcher/chinaTrackerDispatcher";

const DAY_W = 44; // px per day column
const BAR_H = 24;
const BAR_GAP = 4;
const ROW_PAD = 6;
const LEFT_W = 130; // frozen production-line label column
const MONTH_H = 26;
const DAY_H = 24;
const WINDOW_MONTHS = 3;
// A trackpad swipe that runs past the end of the grid otherwise chains to the
// browser and triggers back/forward navigation, which loses the page. Keeping
// the overscroll inside the scroller stops that; it is a no-op off web.
const CONTAIN_OVERSCROLL =
  Platform.OS === "web" ? ({ overscrollBehavior: "contain" } as any) : null;

// The statuses reachable here, risk-first. Cancelled / Closed / Completed are
// absent by design: this board reads the same active set the tracker screen
// lists, which strips them, so chips for them could never match.
// Listed explicitly rather than derived from the data, because On Time /
// At Risk / Delayed are computed from due dates and ETAs — whether any order
// currently holds one varies run to run, and chips that come and go are
// useless as a filter.
const ORDER_STATUSES = [
  "On Time",
  "At Risk",
  "Delayed",
  "Interrupted",
  "Planned",
  "No Data",
];

const STATUS_LABELS: Record<string, keyof typeof translations> = {
  "On Time": "onTime",
  "At Risk": "atRisk",
  Delayed: "delayed",
  Interrupted: "interrupted",
  Planned: "planned",
  "No Data": "noData",
};

// Same traffic-light convention as the tracker's own getStatusColor.
const STATUS_COLORS: Record<string, string> = {
  "On Time": "green",
  "At Risk": "orange",
  Delayed: "red",
};

const EXTEND_MONTHS = 1; // months revealed each time a scroll edge is reached
// Scrolling keeps revealing schedule up to this much; past it the user has to
// ask for more explicitly, so the grid can't grow unbounded just by leaning on
// the trackpad. Confirming resets the allowance for another six months.
const AUTO_EXTEND_LIMIT_MONTHS = 6;
const EDGE_SLACK = 80; // px from an edge that counts as "scrolled to the end"
const MAX_SPAN_DAYS = 730; // keep the rendered grid to a sane number of cells
const POPOVER_W = 320;
const CARET_H = 7;
const POPOVER_EST_H = 240; // fallback until the popover has been measured once

const BAR_COLORS = [
  "#E8A87C", "#C38D9E", "#85CDCB", "#E27D60", "#41B3A3", "#A2C5AC",
  "#F5B7B1", "#AEC6CF", "#D7BDE2", "#F9E79F", "#A9CCE3", "#B9E3C6",
];

const MS_PER_DAY = 86400000;

// Tracker dates are ISO strings stored in UTC. The rest of the China Tracker
// screens render them with moment().local(), so this grid does the same rather
// than pinning a site timezone.
const parseTrackerDate = (value: string | null | undefined) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const toISODate = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;

const formatDisplayDate = (date: Date) =>
  `${String(date.getDate()).padStart(2, "0")}/${String(
    date.getMonth() + 1,
  ).padStart(2, "0")}/${date.getFullYear()}`;

const formatTrackerDate = (value: string | null | undefined) =>
  value ? moment(value).local().format("DD/MM/YYYY") : "—";

const dayDiff = (from: Date, to: Date) =>
  Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / MS_PER_DAY);

const colorFor = (key: string) => {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash << 5) - hash + key.charCodeAt(i);
    hash |= 0;
  }
  return BAR_COLORS[Math.abs(hash) % BAR_COLORS.length];
};

// A run is one production run (an order's `details` entry) paired with the
// order it belongs to, since the bar label needs fields from both.
interface trackerRun {
  order: any;
  run: any;
  key: string;
}

interface positionedRun {
  item: trackerRun;
  startIndex: number;
  endIndex: number;
  lane: number;
}

interface plannedRow {
  key: string;
  name: string;
  bars: Array<positionedRun>;
  height: number;
  top: number;
}

interface popoverAnchor {
  item: trackerRun;
  barTop: number;
  barBottom: number;
  left: number;
}

// Pack overlapping runs into stacked lanes so no two bars collide.
const assignLanes = (runs: Array<positionedRun>) => {
  const laneEnds: Array<number> = [];
  runs
    .slice()
    .sort((a, b) => a.startIndex - b.startIndex)
    .forEach((item) => {
      let lane = laneEnds.findIndex((end) => end <= item.startIndex);
      if (lane === -1) lane = laneEnds.length;
      laneEnds[lane] = item.endIndex + 1;
      item.lane = lane;
    });
  return laneEnds.length;
};

// Production lines are numbers; keep them in numeric order with any
// unnumbered line last.
const compareLines = (a: string, b: string) => {
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  if (!Number.isNaN(na)) return -1;
  if (!Number.isNaN(nb)) return 1;
  return a.localeCompare(b);
};

// An order matches if ANY comma-separated term matches, mirroring the
// Work Orders Planning search.
const matchesSearch = (item: trackerRun, terms: Array<string>) => {
  if (terms.length === 0) return true;
  const haystack = [
    item.order?.MSRef,
    item.order?.OrderNumber1,
    item.order?.ItemCode,
    item.order?.ItemName,
    item.order?.BPName,
    item.order?.StateDelivery,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return terms.some((term) => haystack.includes(term));
};

export default function ChinaTrackerGanttScreen() {
  const { colors, isDark } = useAppTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const dispatch = useDispatch<any>();

  // The same active set the China Order Tracker screen lists, so an order that
  // closes in SAP and drops off that list drops off this board too. Reusing
  // the reducer's own approvedOrders rather than re-filtering here keeps the
  // two screens from drifting apart.
  const { approvedOrders, loading, isChinaTrackerLoaded } = useSelector(
    (state: any) => state.chinaTracker,
  );

  // The visible window is a start/end pair rather than a fixed-width anchor,
  // because scrolling to either edge extends that end rather than shifting.
  const [rangeStart, setRangeStart] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [rangeEnd, setRangeEnd] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + WINDOW_MONTHS, 0);
  });
  const [search, setSearch] = useState<string>("");
  const [selectedStatuses, setSelectedStatuses] = useState<Array<string>>([]);
  const [selected, setSelected] = useState<popoverAnchor | null>(null);
  const [popoverHeight, setPopoverHeight] = useState<number>(POPOVER_EST_H);
  const [bodyHeight, setBodyHeight] = useState<number>(0);
  // Months revealed by scrolling since the last explicit "load more", and the
  // edge that hit the allowance (so the button knows which way to grow).
  const [autoExtended, setAutoExtended] = useState<number>(0);
  const [pendingEdge, setPendingEdge] = useState<"start" | "end" | null>(null);

  // Same source as the China Order Tracker screen, so both stay in step.
  useEffect(() => {
    if (!isChinaTrackerLoaded) {
      dispatch(getMergedTrackerOrders({ refresh: false }));
    }
  }, [isChinaTrackerLoaded, dispatch]);

  const totalDays = useMemo(() => dayDiff(rangeStart, rangeEnd) + 1, [rangeStart, rangeEnd]);
  const gridWidth = totalDays * DAY_W;

  const scrollAnim = useRef(new Animated.Value(0)).current;
  const negScrollAnim = useRef(Animated.multiply(scrollAnim, -1)).current;
  const gridScrollRef = useRef<ScrollView>(null);
  const scrollXRef = useRef(0);
  // Set when we prepend months, so the view can be nudged back by the width
  // that was just inserted on the left and the content appears to stay put.
  const pendingLeftShift = useRef(0);
  // Held in a ref so the Animated.event listener below never goes stale.
  const scrollHandlerRef = useRef<(event: any) => void>(() => {});

  const onBodyScroll = useRef(
    Animated.event([{ nativeEvent: { contentOffset: { x: scrollAnim } } }], {
      useNativeDriver: false,
      listener: (event: any) => scrollHandlerRef.current(event),
    }),
  ).current;

  // Every production run on the listed orders, flattened.
  const allRuns: Array<trackerRun> = useMemo(() => {
    const orders: Array<any> = approvedOrders ?? [];
    return orders.flatMap((order) =>
      (order?.details ?? []).map((run: any, index: number) => ({
        order,
        run,
        key: run?._id ?? `${order?.Key ?? order?.MSRef ?? "order"}-${index}`,
      })),
    );
  }, [approvedOrders]);

  // How far the cached runs actually span. Scrolling stops extending once the
  // window covers everything, so the chart never grows into empty months.
  const dataBounds = useMemo(() => {
    let min: Date | null = null;
    let max: Date | null = null;
    allRuns.forEach(({ run }) => {
      const start =
        parseTrackerDate(run?.actualStartDay) ?? parseTrackerDate(run?.planStartDay);
      const finish =
        parseTrackerDate(run?.actualCompletionDate) ??
        parseTrackerDate(run?.ESTCompletionDate) ??
        start;
      if (start && (!min || start < min)) min = start;
      if (finish && (!max || finish > max)) max = finish;
    });
    return { min, max };
  }, [allRuns]);

  // Always offer the full set, plus anything unexpected the data throws up so
  // no run can end up unfilterable.
  const statusOptions = useMemo(() => {
    const extras = new Set<string>();
    allRuns.forEach((item) => {
      const status = item.order?.OrderStatus;
      if (status && !ORDER_STATUSES.includes(String(status))) {
        extras.add(String(status));
      }
    });
    return [...ORDER_STATUSES, ...Array.from(extras).sort()];
  }, [allRuns]);

  const searchTerms = useMemo(
    () =>
      search
        .split(",")
        .map((term) => term.trim().toLowerCase())
        .filter(Boolean),
    [search],
  );

  const days = useMemo(
    () =>
      Array.from({ length: totalDays }, (_, index) => {
        const date = new Date(
          rangeStart.getFullYear(),
          rangeStart.getMonth(),
          rangeStart.getDate() + index,
        );
        const weekday = date.getDay();
        return { date, isWeekend: weekday === 0 || weekday === 6 };
      }),
    [rangeStart, totalDays],
  );

  const months = useMemo(() => {
    const result: Array<{ label: string; span: number }> = [];
    days.forEach(({ date }) => {
      const label = date.toLocaleDateString("en-AU", { month: "long", year: "numeric" });
      const last = result[result.length - 1];
      if (last && last.label === label) last.span += 1;
      else result.push({ label, span: 1 });
    });
    return result;
  }, [days]);

  const todayIndex = useMemo(() => {
    const index = dayDiff(rangeStart, new Date());
    return index >= 0 && index < totalDays ? index : -1;
  }, [rangeStart, totalDays]);

  const rows: Array<plannedRow> = useMemo(() => {
    const byLine = new Map<string, Array<positionedRun>>();

    allRuns.forEach((item) => {
      if (selectedStatuses.length > 0 && !selectedStatuses.includes(String(item.order?.OrderStatus))) {
        return;
      }
      if (!matchesSearch(item, searchTerms)) return;

      // Draw where the run actually happened once that is known, falling back
      // per field to the plan — so a run that has started but not finished
      // still shows its real start against its estimated completion.
      const actualStart = parseTrackerDate(item.run?.actualStartDay);
      const actualFinish = parseTrackerDate(item.run?.actualCompletionDate);
      const start = actualStart ?? parseTrackerDate(item.run?.planStartDay);
      const finish =
        actualFinish ?? parseTrackerDate(item.run?.ESTCompletionDate) ?? start;
      if (!start || !finish) return;

      // Skip runs entirely outside the visible window; clamp the rest so
      // bars that straddle an edge still render. Mixing an actual start with
      // an estimated completion can invert the range when a run starts late,
      // so normalise before testing the window.
      const rawStart = dayDiff(rangeStart, start);
      const rawEnd = Math.max(rawStart, dayDiff(rangeStart, finish));
      if (rawEnd < 0 || rawStart > totalDays - 1) return;

      const startIndex = Math.max(0, Math.min(rawStart, totalDays - 1));
      const endIndex = Math.max(startIndex, Math.min(rawEnd, totalDays - 1));

      const line = String(item.run?.allocateProductionLine ?? "");
      const key = line === "" || line === "null" || line === "undefined" ? "unassigned" : line;
      if (!byLine.has(key)) byLine.set(key, []);
      byLine.get(key)!.push({ item, startIndex, endIndex, lane: 0 });
    });

    let offset = 0;
    return Array.from(byLine.entries())
      .sort((a, b) => compareLines(a[0], b[0]))
      .map(([key, bars]) => {
        const laneCount = assignLanes(bars);
        const height = Math.max(1, laneCount) * (BAR_H + BAR_GAP) + ROW_PAD * 2;
        const top = offset;
        offset += height;
        return {
          key,
          name: key === "unassigned" ? t("unassigned") : `${t("productionLine")} ${key}`,
          bars,
          height,
          top,
        };
      });
  }, [allRuns, selectedStatuses, searchTerms, rangeStart, totalDays, t]);

  const visibleCount = useMemo(
    () => rows.reduce((sum, row) => sum + row.bars.length, 0),
    [rows],
  );

  const toggleStatus = (status: string) =>
    setSelectedStatuses((current) =>
      current.includes(status)
        ? current.filter((item) => item !== status)
        : [...current, status],
    );

  // Arrows still pan the whole window; scrolling to an edge grows it instead.
  // Panning is a deliberate move to somewhere else, so any parked Load More
  // prompt no longer applies.
  const shiftMonths = (delta: number) => {
    setPendingEdge(null);
    setRangeStart(
      (current) => new Date(current.getFullYear(), current.getMonth() + delta, 1),
    );
    setRangeEnd(
      (current) => new Date(current.getFullYear(), current.getMonth() + 1 + delta, 0),
    );
  };

  // Is there any cached schedule left to reveal at this edge?
  const canExtend = (edge: "start" | "end") => {
    if (totalDays >= MAX_SPAN_DAYS) return false;
    const { min, max } = dataBounds;
    return edge === "start"
      ? Boolean(min) && rangeStart > (min as Date)
      : Boolean(max) && rangeEnd < (max as Date);
  };

  // Reveal more cached schedule at one edge. The runs are already in Redux,
  // so this is purely a wider render — no refetch.
  const extend = (edge: "start" | "end") => {
    if (!canExtend(edge)) return;
    if (edge === "end") {
      setRangeEnd(
        (current) =>
          new Date(current.getFullYear(), current.getMonth() + 1 + EXTEND_MONTHS, 0),
      );
    } else {
      const next = new Date(
        rangeStart.getFullYear(),
        rangeStart.getMonth() - EXTEND_MONTHS,
        1,
      );
      // Prepending shifts every bar right; remember how far so the viewport can
      // be pushed back by the same amount and appear not to move.
      pendingLeftShift.current = dayDiff(next, rangeStart) * DAY_W;
      setRangeStart(next);
    }
  };

  // Scroll-driven growth, capped by the allowance. Once spent, the edge is
  // parked and the user has to press Load More Data to go further.
  const extendByScroll = (edge: "start" | "end") => {
    if (!canExtend(edge)) return;
    if (autoExtended >= AUTO_EXTEND_LIMIT_MONTHS) {
      setPendingEdge(edge);
      return;
    }
    setAutoExtended((current) => current + EXTEND_MONTHS);
    extend(edge);
  };

  const loadMoreData = () => {
    const edge = pendingEdge;
    if (!edge) return;
    setPendingEdge(null);
    setAutoExtended(0); // fresh allowance for the next six months
    extend(edge);
  };

  // Latest closure, re-bound each render for the Animated.event listener.
  scrollHandlerRef.current = (event: any) => {
    const { contentOffset, layoutMeasurement, contentSize } = event.nativeEvent;
    scrollXRef.current = contentOffset.x;
    if (pendingLeftShift.current !== 0) return; // mid-prepend, ignore
    if (contentOffset.x + layoutMeasurement.width >= contentSize.width - EDGE_SLACK) {
      extendByScroll("end");
    } else if (contentOffset.x <= EDGE_SLACK) {
      extendByScroll("start");
    }
  };

  // Restore the scroll position after months are prepended on the left.
  useEffect(() => {
    const shift = pendingLeftShift.current;
    if (shift === 0) return;
    pendingLeftShift.current = 0;
    const target = scrollXRef.current + shift;
    // Wait for the wider grid to lay out before correcting the offset.
    const frame = requestAnimationFrame(() => {
      gridScrollRef.current?.scrollTo({ x: target, animated: false });
      scrollXRef.current = target;
    });
    return () => cancelAnimationFrame(frame);
  }, [rangeStart]);

  // Exports exactly what the chart currently shows — same window, search and
  // status filters — as a flat table rather than a picture of the chart.
  const exportToExcel = () => {
    const data = rows.flatMap((row) =>
      row.bars.map(({ item }) => ({
        [t("productionLine")]: row.name,
        [t("msRef")]: item.order?.MSRef ?? "",
        [t("orderNumber")]: item.order?.OrderNumber1 ?? "",
        [t("itemCode")]: item.order?.ItemCode ?? "",
        [t("itemName")]: item.order?.ItemName ?? "",
        [t("customer")]: item.order?.BPName ?? "",
        [t("orderStatus")]: item.order?.OrderStatus ?? "",
        [t("runOrder")]: item.run?.runOrder ?? "",
        [t("plannedQuantity")]: item.run?.plannedQty ?? "",
        [t("planStartDay")]: formatTrackerDate(item.run?.planStartDay),
        [t("estCompletion")]: formatTrackerDate(item.run?.ESTCompletionDate),
        [t("actualStartDay")]: formatTrackerDate(item.run?.actualStartDay),
        [t("actualCompletionDate")]: formatTrackerDate(item.run?.actualCompletionDate),
        [t("etd")]: formatTrackerDate(item.run?.ETD),
        [t("eta")]: formatTrackerDate(item.run?.ETA),
        [t("dueDate")]: item.order?.RequestDate ?? "",
      })),
    );

    exportDataToExcel(
      data,
      `china_tracker_planning_${toISODate(rangeStart)}_to_${toISODate(rangeEnd)}`,
      "Planning",
    );
  };

  // Total height the rows occupy, and the height the scrollable body needs:
  // an open popover hangs below the last row, and the inner scroll view clips
  // anything past its content, so the content has to make room for it.
  const contentHeight = useMemo(
    () => rows.reduce((sum, row) => sum + row.height, 0),
    [rows],
  );
  const popoverBottom = selected
    ? selected.barBottom + CARET_H + popoverHeight + 8
    : 0;
  const gridHeight = Math.max(contentHeight, popoverBottom);

  const isBusy = loading || !isChinaTrackerLoaded;

  return (
    <View style={styles.container}>
      <View style={styles.toolbar}>
        <Text style={styles.title}>{t("chinaOrderTracker")}</Text>

        <View style={styles.rangeGroup}>
          <Pressable style={styles.navButton} onPress={() => shiftMonths(-1)}>
            <Text style={styles.navButtonText}>‹</Text>
          </Pressable>
          <Text style={styles.rangeText}>
            {formatDisplayDate(rangeStart)} → {formatDisplayDate(rangeEnd)}
          </Text>
          <Pressable style={styles.navButton} onPress={() => shiftMonths(1)}>
            <Text style={styles.navButtonText}>›</Text>
          </Pressable>
        </View>

        {pendingEdge && (
          <Pressable style={styles.loadMoreButton} onPress={loadMoreData}>
            <Feather
              name={pendingEdge === "start" ? "chevrons-left" : "chevrons-right"}
              size={12}
              color="#1976d2"
            />
            <Text style={styles.loadMoreText}>{t("loadMoreData")}</Text>
          </Pressable>
        )}

        <TextInput
          style={styles.searchInput}
          value={search}
          onChangeText={setSearch}
          placeholder={t("search")}
          placeholderTextColor={colors.textMuted}
        />

        <View style={styles.chipGroup}>
          {statusOptions.map((status) => {
            const active = selectedStatuses.includes(status);
            const accent = STATUS_COLORS[status];
            const label = STATUS_LABELS[status];
            return (
              <Pressable
                key={status}
                onPress={() => toggleStatus(status)}
                style={[
                  styles.chip,
                  active && styles.chipActive,
                  active && accent ? { borderColor: accent } : null,
                ]}
              >
                {accent && <View style={[styles.chipDot, { backgroundColor: accent }]} />}
                <Text
                  style={[
                    styles.chipText,
                    active && styles.chipTextActive,
                    active && accent ? { color: accent } : null,
                  ]}
                >
                  {label ? t(label) : status}
                </Text>
              </Pressable>
            );
          })}
        </View>

        <Text style={styles.countText}>
          {visibleCount} {t("productionRuns")}
        </Text>

        <Pressable style={styles.downloadButton} onPress={exportToExcel}>
          <Feather name="download" size={14} color={isDark ? "#aaa" : "#6B7280"} />
        </Pressable>
      </View>

      {isBusy ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#1976d2" />
          <Text style={styles.loadingText}>{t("loading")}</Text>
        </View>
      ) : rows.length === 0 ? (
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>{t("noData")}</Text>
        </View>
      ) : (
        <>
          {/* Frozen month + day header, panned by the body's horizontal scroll */}
          <View style={styles.headerRow}>
            <View style={[styles.cornerCell, { width: LEFT_W }]}>
              <Text style={styles.cornerText}>{t("productionLine")}</Text>
            </View>
            <View style={styles.headerClip}>
              <Animated.View
                style={{ width: gridWidth, transform: [{ translateX: negScrollAnim }] }}
              >
                <View style={styles.monthRow}>
                  {months.map((month, index) => (
                    <View
                      key={`${month.label}-${index}`}
                      style={[styles.monthCell, { width: month.span * DAY_W }]}
                    >
                      <Text style={styles.monthText}>{month.label}</Text>
                    </View>
                  ))}
                </View>
                <View style={styles.dayRow}>
                  {days.map(({ date, isWeekend }, index) => (
                    <View
                      key={index}
                      style={[
                        styles.dayCell,
                        isWeekend && styles.weekendCell,
                        index === todayIndex && styles.todayCell,
                      ]}
                    >
                      <Text style={styles.dayText}>
                        {String(date.getDate()).padStart(2, "0")}
                      </Text>
                    </View>
                  ))}
                </View>
              </Animated.View>
            </View>
          </View>

          {/* Body: line labels stay put horizontally, bars scroll. */}
          <ScrollView
            style={[{ flex: 1 }, CONTAIN_OVERSCROLL]}
            onLayout={(event) => setBodyHeight(event.nativeEvent.layout.height)}
            nestedScrollEnabled
          >
            <View style={{ flexDirection: "row" }}>
              <View style={{ width: LEFT_W }}>
                {rows.map((row) => (
                  <View key={row.key} style={[styles.labelCell, { height: row.height }]}>
                    <Text style={styles.labelText}>{row.name}</Text>
                  </View>
                ))}
              </View>

              <ScrollView
                ref={gridScrollRef}
                horizontal
                style={CONTAIN_OVERSCROLL}
                onScroll={onBodyScroll}
                scrollEventThrottle={16}
                showsHorizontalScrollIndicator
              >
                <View style={{ width: gridWidth, height: gridHeight }}>
                  {rows.map((row) => (
                    <View key={row.key} style={[styles.gridRow, { height: row.height }]}>
                      {days.map(({ isWeekend }, index) => (
                        <View
                          key={index}
                          style={[
                            styles.gridCell,
                            { left: index * DAY_W },
                            isWeekend && styles.weekendCell,
                            index === todayIndex && styles.todayCell,
                          ]}
                        />
                      ))}

                      {row.bars.map(({ item, startIndex, endIndex, lane }) => {
                        const barTop = ROW_PAD + lane * (BAR_H + BAR_GAP);
                        const isComplete = Boolean(item.run?.actualCompletionDate);
                        return (
                          <Pressable
                            key={item.key}
                            onPress={() =>
                              setSelected({
                                item,
                                barTop: row.top + barTop,
                                barBottom: row.top + barTop + BAR_H,
                                left: startIndex * DAY_W,
                              })
                            }
                            style={[
                              styles.bar,
                              {
                                left: startIndex * DAY_W + 1,
                                width: (endIndex - startIndex + 1) * DAY_W - 2,
                                top: barTop,
                                backgroundColor: colorFor(
                                  item.order?.ItemCode ?? item.key,
                                ),
                                opacity: isComplete ? 0.5 : 1,
                                borderColor:
                                  selected?.item.key === item.key ? "#1976d2" : "transparent",
                              },
                            ]}
                          >
                            <Text style={styles.barText} numberOfLines={1}>
                              {item.order?.MSRef} · {item.order?.ItemName}
                            </Text>
                          </Pressable>
                        );
                      })}
                    </View>
                  ))}

                  {/* Anchored info popover — rendered after the rows so it paints
                      above them, and inside the grid so it tracks scroll. */}
                  {selected &&
                    (() => {
                      // Flip above the bar only when there genuinely isn't room
                      // below inside the visible body, and flipping would not
                      // push it off the top instead.
                      const flipAbove =
                        bodyHeight > 0 &&
                        selected.barBottom + CARET_H + popoverHeight > bodyHeight &&
                        selected.barTop - CARET_H - popoverHeight >= 0;
                      const popoverTop = flipAbove
                        ? selected.barTop - CARET_H - popoverHeight
                        : selected.barBottom + CARET_H;
                      const popoverLeft = Math.max(
                        0,
                        Math.min(selected.left, gridWidth - POPOVER_W),
                      );
                      const caretLeft = Math.max(
                        10,
                        Math.min(selected.left - popoverLeft + DAY_W / 2, POPOVER_W - 24),
                      );
                      const { order, run } = selected.item;

                      return (
                        <View
                          style={[styles.popover, { top: popoverTop, left: popoverLeft }]}
                          onLayout={(event) =>
                            setPopoverHeight(event.nativeEvent.layout.height)
                          }
                        >
                          <View
                            style={[
                              flipAbove ? styles.popoverCaretBottom : styles.popoverCaret,
                              { left: caretLeft },
                            ]}
                          />
                          <View style={styles.popoverHeader}>
                            <Text style={styles.popoverTitle}>
                              {[order?.ItemCode, order?.ItemName, order?.MSRef]
                                .filter(Boolean)
                                .join(" - ")}
                            </Text>
                            <Pressable
                              style={styles.popoverClose}
                              onPress={() => setSelected(null)}
                            >
                              <Text style={styles.popoverCloseText}>✕</Text>
                            </Pressable>
                          </View>
                          <View style={styles.popoverBody}>
                            <Text style={styles.popoverRow}>
                              <Text style={styles.popoverLabel}>{t("customer")}: </Text>
                              {order?.BPName ?? t("unknown")}
                            </Text>
                            <Text style={styles.popoverRow}>
                              <Text style={styles.popoverLabel}>{t("orderNumber")}: </Text>
                              {order?.OrderNumber1 ?? t("unknown")}
                            </Text>
                            <Text style={styles.popoverRow}>
                              <Text style={styles.popoverLabel}>{t("runOrder")}: </Text>
                              {run?.runOrder ?? "—"}
                              {"   "}
                              <Text style={styles.popoverLabel}>
                                {t("plannedQuantity")}:{" "}
                              </Text>
                              {run?.plannedQty?.toLocaleString?.() ?? "—"}
                            </Text>
                            <Text style={styles.popoverRow}>
                              <Text style={styles.popoverLabel}>{t("planStartDay")}: </Text>
                              {formatTrackerDate(run?.planStartDay)}
                            </Text>
                            <Text style={styles.popoverRow}>
                              <Text style={styles.popoverLabel}>{t("estCompletion")}: </Text>
                              {formatTrackerDate(run?.ESTCompletionDate)}
                            </Text>
                            <Text style={styles.popoverRow}>
                              <Text style={styles.popoverLabel}>{t("actualStartDay")}: </Text>
                              {formatTrackerDate(run?.actualStartDay)}
                            </Text>
                            <Text style={styles.popoverRow}>
                              <Text style={styles.popoverLabel}>
                                {t("actualCompletionDate")}:{" "}
                              </Text>
                              {formatTrackerDate(run?.actualCompletionDate)}
                            </Text>
                            <Text style={styles.popoverRow}>
                              <Text style={styles.popoverLabel}>{t("etd")}: </Text>
                              {formatTrackerDate(run?.ETD)}
                              {"   "}
                              <Text style={styles.popoverLabel}>{t("eta")}: </Text>
                              {formatTrackerDate(run?.ETA)}
                            </Text>
                            <Text style={styles.popoverRow}>
                              <Text style={styles.popoverLabel}>{t("dueDate")}: </Text>
                              {order?.RequestDate ?? "—"}
                              {"   "}
                              <Text style={styles.popoverLabel}>{t("orderStatus")}: </Text>
                              {order?.OrderStatus ?? "—"}
                            </Text>
                          </View>
                        </View>
                      );
                    })()}
                </View>
              </ScrollView>
            </View>
          </ScrollView>
        </>
      )}
    </View>
  );
}

const createStyles = (colors: any, isDark: boolean) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: isDark ? colors.background : "#f5f5f5",
      padding: 12,
    },
    toolbar: {
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 10,
      marginBottom: 10,
    },
    title: {
      fontSize: 16,
      fontWeight: "700",
      color: colors.text,
    },
    rangeGroup: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
    },
    navButton: {
      paddingHorizontal: 10,
      paddingVertical: 2,
      borderRadius: 4,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    navButtonText: {
      fontSize: 16,
      lineHeight: 20,
      color: colors.text,
    },
    rangeText: {
      fontSize: 12,
      color: colors.textSecondary,
      minWidth: 190,
      textAlign: "center",
    },
    searchInput: {
      minWidth: 200,
      flexGrow: 1,
      maxWidth: 360,
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: 4,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      color: colors.text,
      fontSize: 12,
    },
    chipGroup: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 6,
    },
    chip: {
      flexDirection: "row",
      alignItems: "center",
      gap: 5,
      paddingHorizontal: 10,
      paddingVertical: 3,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    chipDot: {
      width: 6,
      height: 6,
      borderRadius: 3,
    },
    chipActive: {
      borderColor: "#1976d2",
      backgroundColor: isDark ? "#123" : "#e3f2fd",
    },
    chipText: {
      fontSize: 11,
      color: colors.textMuted,
    },
    chipTextActive: {
      color: "#1976d2",
      fontWeight: "700",
    },
    loadMoreButton: {
      flexDirection: "row",
      alignItems: "center",
      gap: 5,
      paddingHorizontal: 10,
      paddingVertical: 4,
      borderRadius: 4,
      borderWidth: 1,
      borderColor: "#1976d2",
      backgroundColor: isDark ? "#123" : "#e3f2fd",
    },
    loadMoreText: {
      fontSize: 11,
      fontWeight: "700",
      color: "#1976d2",
    },
    countText: {
      fontSize: 11,
      color: colors.textMuted,
      marginLeft: "auto",
    },
    downloadButton: {
      paddingHorizontal: 8,
      paddingVertical: 6,
      borderRadius: 4,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    loadingContainer: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
    },
    loadingText: {
      marginTop: 10,
      fontSize: 12,
      color: colors.textMuted,
    },
    headerRow: {
      flexDirection: "row",
      borderTopWidth: 1,
      borderLeftWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    headerClip: {
      flex: 1,
      overflow: "hidden",
    },
    cornerCell: {
      height: MONTH_H + DAY_H,
      justifyContent: "center",
      paddingLeft: 8,
      borderRightWidth: 1,
      borderColor: colors.border,
    },
    cornerText: {
      fontSize: 11,
      fontWeight: "700",
      color: colors.textSecondary,
    },
    monthRow: {
      flexDirection: "row",
      height: MONTH_H,
    },
    monthCell: {
      height: MONTH_H,
      justifyContent: "center",
      paddingLeft: 6,
      borderRightWidth: 1,
      borderBottomWidth: 1,
      borderColor: colors.border,
    },
    monthText: {
      fontSize: 11,
      fontWeight: "700",
      color: colors.textSecondary,
    },
    dayRow: {
      flexDirection: "row",
      height: DAY_H,
    },
    dayCell: {
      width: DAY_W,
      height: DAY_H,
      justifyContent: "center",
      alignItems: "center",
      borderRightWidth: 1,
      borderColor: colors.borderSubtle,
    },
    dayText: {
      fontSize: 10,
      color: colors.textMuted,
    },
    weekendCell: {
      backgroundColor: isDark ? "#1b1b1b" : "#eeeeee",
    },
    todayCell: {
      backgroundColor: isDark ? "#2a2410" : "#fff8e1",
    },
    labelCell: {
      justifyContent: "center",
      paddingLeft: 8,
      borderLeftWidth: 1,
      borderBottomWidth: 1,
      borderRightWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    labelText: {
      fontSize: 12,
      color: colors.text,
    },
    gridRow: {
      position: "relative",
      borderBottomWidth: 1,
      borderColor: colors.border,
    },
    gridCell: {
      position: "absolute",
      top: 0,
      bottom: 0,
      width: DAY_W,
      borderRightWidth: 1,
      borderColor: colors.borderSubtle,
    },
    bar: {
      position: "absolute",
      height: BAR_H,
      borderRadius: 3,
      borderWidth: 1,
      justifyContent: "center",
      paddingHorizontal: 5,
      overflow: "hidden",
    },
    barText: {
      fontSize: 10,
      color: "#2b2b2b",
    },
    popover: {
      position: "absolute",
      width: POPOVER_W,
      zIndex: 20,
      borderRadius: 6,
      backgroundColor: isDark ? "#2b2b2b" : "#ffffff",
      shadowColor: "#000",
      shadowOpacity: 0.25,
      shadowRadius: 10,
      shadowOffset: { width: 0, height: 4 },
      elevation: 8,
    },
    popoverCaret: {
      position: "absolute",
      top: -CARET_H,
      width: 0,
      height: 0,
      borderLeftWidth: CARET_H,
      borderRightWidth: CARET_H,
      borderBottomWidth: CARET_H,
      borderLeftColor: "transparent",
      borderRightColor: "transparent",
      borderBottomColor: isDark ? "#3a3a3a" : "#e9e9ea",
    },
    popoverCaretBottom: {
      position: "absolute",
      bottom: -CARET_H,
      width: 0,
      height: 0,
      borderLeftWidth: CARET_H,
      borderRightWidth: CARET_H,
      borderTopWidth: CARET_H,
      borderLeftColor: "transparent",
      borderRightColor: "transparent",
      borderTopColor: isDark ? "#2b2b2b" : "#ffffff",
    },
    popoverHeader: {
      flexDirection: "row",
      alignItems: "flex-start",
      padding: 12,
      borderTopLeftRadius: 6,
      borderTopRightRadius: 6,
      backgroundColor: isDark ? "#3a3a3a" : "#e9e9ea",
    },
    popoverTitle: {
      flex: 1,
      fontSize: 13,
      fontWeight: "700",
      lineHeight: 18,
      color: colors.text,
      paddingRight: 8,
    },
    popoverClose: {
      paddingHorizontal: 4,
    },
    popoverCloseText: {
      fontSize: 13,
      fontWeight: "700",
      color: colors.text,
    },
    popoverBody: {
      padding: 12,
      gap: 4,
    },
    popoverRow: {
      fontSize: 12,
      color: colors.text,
    },
    popoverLabel: {
      fontWeight: "700",
      color: colors.text,
    },
  });
